/**
 * Shared accessibility utilities for Shopify (no bundler).
 * Attaches to window._theme for legacy callers; also exposes window.CommonAda.
 */

(function (window, document) {
  'use strict';

  /** Native / explicit controls that participate in keyboard tab order. */
  var TABBABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
    'summary',
    'iframe',
    'object',
    'embed',
    'video[controls]',
    'audio[controls]',
  ].join(',');

  /** Broader query (matches custom-ada.js); includes role widgets and [href] on custom nodes. */
  var FOCUSABLE_SELECTOR = [
    'button',
    '[href]',
    'input',
    'select',
    'textarea',
    '[tabindex="0"]',
    'iframe',
    'object',
    'embed',
    '[tabindex]:not([tabindex="-1"])',
    '[role="button"]',
    '[role="link"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="tab"]',
    '[role="menuitem"]',
    '[role="option"]',
  ].join(',');

  /** Union used when querying; results are filtered with isElementTabbable. */
  var INTERACTIVE_SELECTOR = TABBABLE_SELECTOR + ',' + FOCUSABLE_SELECTOR;

  var zoomResizeTimer;
  var savedFocusElement = null;
  var liveRegionEl = null;

  /**
   * @param {Element|string|{jquery?: boolean, length?: number}|null} target
   * @returns {Element|null}
   */
  function resolveElement(target) {
    if (!target) return null;
    if (target.nodeType === 1) return target;
    if (typeof target === 'string') return document.querySelector(target);
    if (target.jquery && target[0]) return target[0];
    if (target.length && target[0] && target[0].nodeType === 1) return target[0];
    return null;
  }

  function safeFocus(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (err) {
      el.focus();
    }
  }

  /**
   * @param {Element} el
   * @returns {boolean}
   */
  function isElementVisible(el) {
    if (!el || el.getAttribute('aria-hidden') === 'true') return false;
    if (el.hasAttribute('hidden') || el.disabled) return false;

    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    /* Keep opacity-0 controls (e.g. mini-cart overlay dismiss) in the tab order. */
    if (parseFloat(style.opacity) === 0 && style.pointerEvents === 'none') return false;

    var rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;

    if (!el.getClientRects().length && style.position !== 'fixed') return false;
    return true;
  }

  /**
   * Whether an element can receive focus via Tab (after visibility/disabled checks).
   * @param {Element} el
   * @returns {boolean}
   */
  function isElementTabbable(el) {
    if (!isElementVisible(el)) return false;

    var tag = el.tagName;
    var tabIndexAttr = el.getAttribute('tabindex');
    var hasTabIndexAttr = tabIndexAttr !== null && tabIndexAttr !== '';

    if (hasTabIndexAttr) {
      var parsed = parseInt(tabIndexAttr, 10);
      return !isNaN(parsed) && parsed >= 0;
    }

    if (tag === 'A' || tag === 'AREA') return el.hasAttribute('href');
    if (tag === 'BUTTON' || tag === 'SELECT' || tag === 'TEXTAREA') return true;
    if (tag === 'INPUT') return el.type !== 'hidden';
    if (tag === 'SUMMARY') return true;
    if (el.isContentEditable) return true;
    if (tag === 'IFRAME' || tag === 'OBJECT' || tag === 'EMBED') return true;
    if (tag === 'VIDEO' || tag === 'AUDIO') return el.hasAttribute('controls');

    var role = el.getAttribute('role');
    if (
      role === 'button' ||
      role === 'link' ||
      role === 'checkbox' ||
      role === 'radio' ||
      role === 'tab' ||
      role === 'menuitem' ||
      role === 'option'
    ) {
      return el.tabIndex >= 0;
    }

    if (el.hasAttribute('href')) return true;

    return false;
  }

  /**
   * @param {string|string[]|undefined} additional
   * @returns {string}
   */
  function buildTabbableSelector(additional) {
    if (!additional) return INTERACTIVE_SELECTOR;
    var extra = Array.isArray(additional) ? additional.join(',') : String(additional);
    return extra ? INTERACTIVE_SELECTOR + ',' + extra : INTERACTIVE_SELECTOR;
  }

  function pushUnique(list, el) {
    for (var i = 0; i < list.length; i++) {
      if (list[i] === el) return;
    }
    list.push(el);
  }

  /**
   * @param {Element|string|{jquery?: boolean}} container
   * @param {string|string[]|undefined} additionalSelectors
   * @returns {Element[]}
   */
  function getTabbableElements(container, additionalSelectors) {
    var root = resolveElement(container);
    if (!root || typeof root.querySelectorAll !== 'function') return [];

    var selector = buildTabbableSelector(additionalSelectors);
    var nodes = root.querySelectorAll(selector);
    var tabbables = [];
    for (var i = 0; i < nodes.length; i++) {
      if (isElementTabbable(nodes[i])) pushUnique(tabbables, nodes[i]);
    }
    return tabbables;
  }

  /**
   * Elements that can receive programmatic focus (includes tabindex="-1").
   * @param {Element|string} container
   * @returns {Element[]}
   */
  function getFocusableElements(container) {
    var root = resolveElement(container);
    if (!root || typeof root.querySelectorAll !== 'function') return [];

    var nodes = root.querySelectorAll(INTERACTIVE_SELECTOR);
    var focusables = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!isElementVisible(el)) continue;
      if (isElementTabbable(el)) {
        pushUnique(focusables, el);
        continue;
      }
      if (el.hasAttribute('tabindex') && el.getAttribute('tabindex') === '-1') {
        pushUnique(focusables, el);
      }
    }
    return focusables;
  }

  /**
   * Remember a cart line qty/remove control before Liquid Ajax Cart replaces the DOM.
   * @param {Element} el
   * @returns {{ type: string, lineIndex: string, action: string }|null}
   */
  function captureLineItemControlFocus(el) {
    if (!el || !el.closest) return null;
    var lineItem = el.closest('.line-item');
    if (!lineItem) return null;

    var lineIndex = lineItem.getAttribute('data-index');
    if (lineIndex === null || lineIndex === '') return null;

    var action = 'request';
    if (el.classList && el.classList.contains('line-item__qty-button--increase')) {
      action = 'increase';
    } else if (el.classList.contains('line-item__qty-button--decrease')) {
      action = 'decrease';
    } else if (el.classList.contains('line-item__remove-link')) {
      action = 'remove';
    }

    return { type: 'line-item-control', lineIndex: lineIndex, action: action };
  }

  /**
   * Find the matching control in updated cart markup and focus it.
   * @param {Element|string} container
   * @param {{ type: string, lineIndex: string, action: string }|null} descriptor
   * @returns {Element|null}
   */
  function restoreLineItemControlFocus(container, descriptor) {
    var root = resolveElement(container);
    if (!root || !descriptor || descriptor.type !== 'line-item-control') return null;

    function drawerClose() {
      return (
        root.querySelector('.mini-cart__close') ||
        root.querySelector('[data-ajax-cart-static-element="close-minicart"]')
      );
    }

    if (descriptor.action === 'remove') {
      var closeBtn = drawerClose();
      if (closeBtn) safeFocus(closeBtn);
      return closeBtn;
    }

    var line = root.querySelector('.line-item[data-index="' + descriptor.lineIndex + '"]');
    if (!line) {
      closeBtn = drawerClose();
      if (closeBtn) safeFocus(closeBtn);
      return closeBtn;
    }

    var selector =
      descriptor.action === 'increase'
        ? '.line-item__qty-button--increase'
        : descriptor.action === 'decrease'
          ? '.line-item__qty-button--decrease'
          : '[data-ajax-cart-request-button]';

    var target = line.querySelector(selector);
    if (!target || !isElementTabbable(target)) {
      target = line.querySelector('[data-ajax-cart-request-button]');
    }

    if (target && isElementTabbable(target)) {
      safeFocus(target);
      return target;
    }

    closeBtn = drawerClose();
    if (closeBtn) safeFocus(closeBtn);
    return closeBtn;
  }

  /**
   * @param {Element|string} container
   * @param {string|string[]|undefined} additionalSelectors
   * @returns {Element|null}
   */
  function focusFirstTabbable(container, additionalSelectors) {
    var tabbables = getTabbableElements(container, additionalSelectors);
    if (!tabbables.length) return null;
    safeFocus(tabbables[0]);
    return tabbables[0];
  }

  /**
   * Remember the element to restore when a modal/dialog closes.
   * @returns {Element|null}
   */
  function saveFocus() {
    var active = document.activeElement;
    savedFocusElement =
      active && active !== document.body && typeof active.focus === 'function' ? active : null;
    return savedFocusElement;
  }

  /**
   * @returns {boolean}
   */
  function restoreFocus() {
    if (!savedFocusElement || typeof savedFocusElement.focus !== 'function') {
      savedFocusElement = null;
      return false;
    }
    safeFocus(savedFocusElement);
    savedFocusElement = null;
    return true;
  }

  /**
   * Trap keyboard focus inside a container (modal, drawer, popup).
   *
   * @param {Element|string} container
   * @param {function(Element, KeyboardEvent)|undefined} onEscape - called on Escape
   * @param {string|string[]|Object|undefined} thirdArg - extra selectors OR options object
   * @param {string|string[]} [thirdArg.additionalSelectors]
   * @param {Element} [thirdArg.initialFocus]
   * @param {boolean} [thirdArg.focusOnActivate=true]
   * @returns {function(): void} deactivate - removes listeners; call when closing
   */
  function focusTrap(container, onEscape, thirdArg) {
    var root = resolveElement(container);
    if (!root) return function () {};

    var options =
      thirdArg && typeof thirdArg === 'object' && !Array.isArray(thirdArg) && !thirdArg.nodeType
        ? thirdArg
        : { additionalSelectors: thirdArg };

    var additionalSelectors = options.additionalSelectors;
    var focusOnActivate = options.focusOnActivate !== false;

    function refreshTabbables() {
      return getTabbableElements(root, additionalSelectors);
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (typeof onEscape === 'function') onEscape(root, e);
        return;
      }
      if (e.key !== 'Tab') return;

      var tabbables = refreshTabbables();
      if (!tabbables.length) {
        e.preventDefault();
        return;
      }

      var first = tabbables[0];
      var last = tabbables[tabbables.length - 1];
      var active = document.activeElement;

      if (tabbables.length === 1) {
        e.preventDefault();
        safeFocus(first);
        return;
      }

      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          safeFocus(last);
        }
      } else if (active === last || !root.contains(active)) {
        e.preventDefault();
        safeFocus(first);
      }
    }

    function handleFocusIn(e) {
      if (!root.contains(e.target)) {
        var trapped = refreshTabbables();
        if (trapped.length) safeFocus(trapped[0]);
      }
    }

    if (focusOnActivate) {
      if (options.initialFocus && root.contains(options.initialFocus)) {
        safeFocus(options.initialFocus);
      } else {
        focusFirstTabbable(root, additionalSelectors);
      }
    }

    root.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);

    return function deactivate() {
      root.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }

  /**
   * Apply zoom / device classes on <html> for responsive zoom CSS.
   */
  function initZoomLevel() {
    if (typeof document === 'undefined') return;

    var html = document.documentElement;
    if (!html) return;

    var classes = Array.from(html.classList);
    for (var i = 0; i < classes.length; i++) {
      if (classes[i].indexOf('device--') > -1) html.classList.remove(classes[i]);
    }

    var ratio = window.innerWidth > 0 ? window.outerWidth / window.innerWidth : 1;
    var browserZoomLevel = Math.round(ratio * 100);

    html.classList.remove('mode--zoom', 'zl-200', 'zl-400');

    if (browserZoomLevel >= 200 && browserZoomLevel < 400) {
      html.classList.add('mode--zoom', 'zl-200');
    } else if (browserZoomLevel >= 400) {
      html.classList.add('mode--zoom', 'zl-400');
    }

    if (window.screen && window.screen.availWidth > 767) {
      html.classList.add('device--ipadandup');
    } else {
      html.classList.add('device--mobile');
    }
  }

  function debouncedInitZoomLevel() {
    window.clearTimeout(zoomResizeTimer);
    zoomResizeTimer = window.setTimeout(initZoomLevel, 150);
  }

  /**
   * @param {string} message
   * @param {'polite'|'assertive'} [priority='polite']
   */
  function announce(message, priority) {
    if (!message) return;

    if (!liveRegionEl || !liveRegionEl.parentNode) {
      liveRegionEl = document.createElement('div');
      liveRegionEl.setAttribute('role', 'status');
      liveRegionEl.setAttribute('aria-live', priority === 'assertive' ? 'assertive' : 'polite');
      liveRegionEl.setAttribute('aria-atomic', 'true');
      liveRegionEl.className = 'visually-hidden';
      document.body.appendChild(liveRegionEl);
    } else {
      liveRegionEl.setAttribute('aria-live', priority === 'assertive' ? 'assertive' : 'polite');
    }

    liveRegionEl.textContent = '';
    window.setTimeout(function () {
      liveRegionEl.textContent = message;
    }, 30);
  }

  /**
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init() {
    initZoomLevel();
    window.addEventListener('resize', debouncedInitZoomLevel);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', debouncedInitZoomLevel);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var api = {
    resolveElement: resolveElement,
    isElementVisible: isElementVisible,
    isElementTabbable: isElementTabbable,
    getTabbableElements: getTabbableElements,
    getFocusableElements: getFocusableElements,
    captureLineItemControlFocus: captureLineItemControlFocus,
    restoreLineItemControlFocus: restoreLineItemControlFocus,
    safeFocus: safeFocus,
    focusFirstTabbable: focusFirstTabbable,
    focusTrap: focusTrap,
    saveFocus: saveFocus,
    restoreFocus: restoreFocus,
    initZoomLevel: initZoomLevel,
    announce: announce,
    prefersReducedMotion: prefersReducedMotion,
    TABBABLE_SELECTOR: TABBABLE_SELECTOR,
    FOCUSABLE_SELECTOR: FOCUSABLE_SELECTOR,
    INTERACTIVE_SELECTOR: INTERACTIVE_SELECTOR,
  };

  window._theme = window._theme || {};
  window._theme.focusTrap = focusTrap;
  window._theme.focusFirstTabbable = focusFirstTabbable;
  window._theme.initZoomLevel = initZoomLevel;
  window._theme.saveFocus = saveFocus;
  window._theme.restoreFocus = restoreFocus;
  window._theme.getTabbableElements = getTabbableElements;
  window._theme.safeFocus = safeFocus;
  window._theme.captureLineItemControlFocus = captureLineItemControlFocus;
  window._theme.restoreLineItemControlFocus = restoreLineItemControlFocus;
  window._theme.announce = announce;

  window.CommonAda = api;
})(window, document);
